import { GetUserResponse, UpdateCompanyUserRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

/** The member being changed, and the fields to change. */
type UpdateCompanyUserVariables = {
    companyUserId: string;
    data: UpdateCompanyUserRequest;
};

type UseUpdateCompanyUserActionProps = {
    /**
     * What to say when the save succeeds, as a whole sentence — "Guardian
     * notifications updated" rather than "Member updated". One endpoint backs
     * every section of the member settings page, and only the caller knows
     * which one it is.
     *
     * A sentence rather than a noun to slot into a template, because Spanish
     * agrees the participle with it: *notificaciones actualizadas* but *modo
     * actualizado*. Defaults to the generic.
     */
    successMessage?: string;
};

type UseUpdateCompanyUserActionData = {
    updateCompanyUser: (
        companyUserId: string,
        data: UpdateCompanyUserRequest
    ) => Promise<GetUserResponse>;
    loading: boolean;
    error: string;
};

/**
 * Changing a member's own details — their name, phone, notification
 * preferences, emergency information and time settings.
 *
 * Carries no `isAllowed` of its own: whether the viewer may edit this member is
 * `useEditProfileAction`, which gates the whole page of sections.
 *
 * Raises both toasts itself. On failure the reason is the information — the
 * caller pressing Save already knows what it was saving — so the specific
 * refusal is shown. On success the reason is nothing and what was saved is
 * everything, which is what `successMessage` is for.
 */
export function useUpdateCompanyUserAction(
    props: UseUpdateCompanyUserActionProps = {}
): UseUpdateCompanyUserActionData {
    const { successMessage } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ companyUserId, data }: UpdateCompanyUserVariables) =>
            openApiRef.current.users
                .updateUser(companyUserId, data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateCompanyUsersCache(queryClient, data.data.user);

            UIToast.success(
                successMessage ?? t('actions.update_company_user.success')
            );
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        updateCompanyUser: (
            companyUserId: string,
            data: UpdateCompanyUserRequest
        ): Promise<GetUserResponse> =>
            mutation.mutateAsync({ companyUserId, data }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
